import React, { useState, useEffect, useContext, useMemo } from "react";
import { toast } from "react-toastify";
import useApi from "../../../../utils/api-manager/Helper/useApi";
import AuthContext from "../../../../utils/secure-route/AuthContext";
import { appConfigs } from "../../../common/appRoutes";
import "./Configuration.css";

const ACTION_ORDER = ["view", "add", "change", "delete"];

// Page-level permission codes are namespaced under the module code so they
// never collide with the existing "All pages in this app" module-wide
// codes (see access.py / useMyPermissions - groups saved before this change
// only ever set module-wide codes, and must keep working exactly as before).
const pagePermissionCode = (serviceName, pagePath, action) => `${serviceName}:page:${pagePath}.${action}`;

// Most apps' "Settings" entry (and a few others, like Inventory's
// "Purchase") is a dropdown with no path of its own - its real pages live
// one level down in `submenu`. Flatten menuItems so every actual navigable
// page shows up as its own row, including Settings sub-pages, instead of
// silently disappearing because the parent has no direct path.
const flattenMenuItems = (menuItems) => {
  const pages = [];
  (menuItems || []).forEach((mi) => {
    if (!mi.name) return;
    if (mi.path) {
      pages.push({ name: mi.name, path: mi.path });
    }
    if (Array.isArray(mi.submenu)) {
      mi.submenu.forEach((sub) => {
        if (sub.name && sub.path) {
          pages.push({ name: `${mi.name}: ${sub.name}`, path: sub.path });
        }
      });
    }
  });
  return pages;
};

// Cross-reference the backend's flat module catalog (permissionCatalog API)
// with appConfigs (the app switcher's own source of truth for names/icons/
// pages) so store owners see "App (with icon) -> its pages" instead of a
// bare list of internal service names when building a group.
const buildAppGroupedCatalog = (flatCatalog) => {
  const modulesByService = {};
  (flatCatalog || []).forEach((category) => {
    (category.modules || []).forEach((mod) => {
      modulesByService[mod.service_name] = mod;
    });
  });

  const grouped = [];
  const usedServices = new Set();

  Object.values(appConfigs || {}).forEach((app) => {
    if (!app.permissionModule) return; // apps with no gated module (e.g. Paisavara home) are skipped
    const mod = modulesByService[app.permissionModule];
    if (!mod) return; // catalog and appConfigs are out of sync for this app - skip rather than show a broken row
    usedServices.add(app.permissionModule);
    grouped.push({
      appKey: app.key,
      appName: app.displayName || app.name,
      logo: app.logo,
      pageItems: flattenMenuItems(app.menuItems),
      mod,
    });
  });

  // Anything in the backend catalog that isn't mapped to a top-level app yet
  // (e.g. Payment Terminal, POS Printer, Cash Drawer live inside Store
  // Settings, not their own app) still needs to be selectable - show them
  // under a generic "Other" bucket instead of hiding them.
  const other = [];
  (flatCatalog || []).forEach((category) => {
    (category.modules || []).forEach((mod) => {
      if (!usedServices.has(mod.service_name)) {
        other.push({ ...mod, category: category.category });
      }
    });
  });

  return { grouped, other };
};

const PermissionGroupsSettings = () => {
  const { Get, Post, Patch, Delete, getAPI } = useApi();
  const { user } = useContext(AuthContext);
  const storeId = user?.store;

  const [activeTab, setActiveTab] = useState("groups"); // "groups" | "members"

  const [catalog, setCatalog] = useState([]);
  const [groups, setGroups] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);

  const [showGroupForm, setShowGroupForm] = useState(false);
  const [editingGroupId, setEditingGroupId] = useState(null);
  const [groupName, setGroupName] = useState("");
  const [selectedPermissions, setSelectedPermissions] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [expandedApiOps, setExpandedApiOps] = useState({}); // { [appKey]: boolean }

  const [memberDraftGroups, setMemberDraftGroups] = useState({}); // { membershipId: Set(groupId) }
  const [savingMemberId, setSavingMemberId] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [catalogRes, groupsRes, membersRes] = await Promise.all([
        Get("permissionCatalog"),
        Get("storePermissionGroups"),
        Get("storeMemberships"),
      ]);
      setCatalog(Array.isArray(catalogRes) ? catalogRes : []);

      const groupList = Array.isArray(groupsRes?.results) ? groupsRes.results : (Array.isArray(groupsRes) ? groupsRes : []);
      const scopedGroups = storeId ? groupList.filter((g) => String(g.store) === String(storeId)) : groupList;
      setGroups(scopedGroups);

      const memberList = Array.isArray(membersRes?.results) ? membersRes.results : (Array.isArray(membersRes) ? membersRes : []);
      const scopedMembers = storeId ? memberList.filter((m) => String(m.store) === String(storeId)) : memberList;
      setMembers(scopedMembers);

      const draft = {};
      scopedMembers.forEach((m) => {
        draft[m.id] = new Set((m.permission_groups || []).map(String));
      });
      setMemberDraftGroups(draft);
    } catch (err) {
      console.error("Error loading permission groups:", err);
      toast.error("Could not load Permission Groups. Please refresh and try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId]);

  const resetGroupForm = () => {
    setEditingGroupId(null);
    setGroupName("");
    setSelectedPermissions(new Set());
    setShowGroupForm(false);
  };

  const startEditGroup = (group) => {
    setEditingGroupId(group.id);
    setGroupName(group.name || "");
    setSelectedPermissions(new Set(group.permissions || []));
    setShowGroupForm(true);
  };

  const togglePermission = (code) => {
    setSelectedPermissions((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  const toggleAllActionsForModule = (moduleActions, checked) => {
    setSelectedPermissions((prev) => {
      const next = new Set(prev);
      moduleActions.forEach((a) => {
        if (checked) next.add(a.code);
        else next.delete(a.code);
      });
      return next;
    });
  };

  const handleSaveGroup = async (e) => {
    e.preventDefault();
    if (!groupName.trim()) {
      toast.error("Give this permission group a name first.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        store: storeId,
        name: groupName.trim(),
        permissions: Array.from(selectedPermissions),
        is_active: true,
      };
      if (editingGroupId) {
        await Patch("storePermissionGroups", editingGroupId, payload);
        toast.success("Permission group updated.");
      } else {
        await Post("storePermissionGroups", payload);
        toast.success("Permission group created.");
      }
      resetGroupForm();
      fetchAll();
    } catch (err) {
      console.error("Error saving permission group:", err);
      toast.error(err?.response?.data?.detail || "Could not save this permission group.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteGroup = async (group) => {
    if (!window.confirm(`Delete the "${group.name}" permission group? Members in it will lose whatever access it granted.`)) {
      return;
    }
    try {
      await Delete("storePermissionGroups", group.id);
      toast.success("Permission group deleted.");
      fetchAll();
    } catch (err) {
      console.error("Error deleting permission group:", err);
      toast.error(err?.response?.data?.detail || "Could not delete this permission group.");
    }
  };

  const toggleMemberGroup = (membershipId, groupId) => {
    setMemberDraftGroups((prev) => {
      const current = new Set(prev[membershipId] || []);
      const key = String(groupId);
      if (current.has(key)) current.delete(key);
      else current.add(key);
      return { ...prev, [membershipId]: current };
    });
  };

  const handleSaveMemberGroups = async (membership) => {
    setSavingMemberId(membership.id);
    try {
      const groupIds = Array.from(memberDraftGroups[membership.id] || []);
      await Patch("storeMemberships", membership.id, { permission_groups: groupIds });
      toast.success(`Updated access for ${membership.user_name || membership.user_email}.`);
      fetchAll();
    } catch (err) {
      console.error("Error updating member's permission groups:", err);
      toast.error(err?.response?.data?.detail || "Could not update this member's permission groups.");
    } finally {
      setSavingMemberId(null);
    }
  };

  const groupsById = useMemo(() => {
    const map = {};
    groups.forEach((g) => { map[g.id] = g; });
    return map;
  }, [groups]);

  const { grouped: appGroupedCatalog, other: otherModules } = useMemo(
    () => buildAppGroupedCatalog(catalog),
    [catalog]
  );

  return (
    <div className="responsive-box store-config-panel">
      <div className="store-config-header">
        <div>
          <span className="store-config-kicker">Access Control</span>
          <h2>Permission Groups</h2>
          <p>
            Build named groups of permissions (e.g. "Cashier", "Store Manager"), then add or remove team
            members from a group instead of assigning permissions to each person one by one. A member with
            no group assigned keeps their normal role access; once you put them in a group, they can only
            do what that group allows.
          </p>
        </div>
      </div>

      <div style={{ display: "flex", gap: "10px", marginBottom: "22px", borderBottom: "1px solid #e5eaf3" }}>
        {[
          { id: "groups", label: "Permission Groups" },
          { id: "members", label: "Team Members" },
        ].map((tabItem) => (
          <button
            key={tabItem.id}
            type="button"
            onClick={() => setActiveTab(tabItem.id)}
            style={{
              padding: "10px 18px",
              border: "none",
              background: "none",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: 800,
              color: activeTab === tabItem.id ? "#2e7d32" : "#64748b",
              borderBottom: activeTab === tabItem.id ? "3px solid #2e7d32" : "3px solid transparent",
            }}
          >
            {tabItem.label}
          </button>
        ))}
      </div>

      {loading && <p style={{ color: "#64748b" }}>Loading...</p>}

      {!loading && activeTab === "groups" && (
        <>
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "16px" }}>
            {!showGroupForm && (
              <button
                type="button"
                className="store-config-save"
                style={{ minWidth: "170px" }}
                onClick={() => setShowGroupForm(true)}
              >
                + New Permission Group
              </button>
            )}
          </div>

          {showGroupForm && (
            <form onSubmit={handleSaveGroup} style={{ marginBottom: "28px" }}>
              <div className="store-config-field" style={{ gridTemplateColumns: "minmax(150px, 1fr) minmax(210px, 340px)", marginBottom: "16px" }}>
                <div>
                  <label>Group name *</label>
                  <span>e.g. "Cashier", "Store Manager", "Bookkeeper"</span>
                </div>
                <input
                  type="text"
                  value={groupName}
                  onChange={(e) => setGroupName(e.target.value)}
                  placeholder="Group name"
                  required
                />
              </div>

              <div style={{ background: "#f8fafc", border: "1px solid #e5eaf3", borderRadius: "16px", padding: "18px", marginBottom: "16px" }}>
                <p style={{ margin: "0 0 6px", fontSize: "14px", fontWeight: 800, color: "#172033" }}>
                  What can members of this group do?
                </p>
                <p style={{ margin: "0 0 14px", fontSize: "12px", color: "#7c8aa1" }}>
                  Check "All pages in this app" to grant the whole app. To restrict a member to only certain
                  pages inside an app, leave "All pages" unchecked and check just the pages they should see —
                  once any individual page is checked, members of this group will only be able to open the
                  pages you've checked for that app.
                </p>
                {catalog.length === 0 && (
                  <p style={{ color: "#94a3b8", fontSize: "13px" }}>No modules available yet — refresh the page.</p>
                )}

                {appGroupedCatalog.map(({ appKey, appName, logo, pageItems, mod }) => {
                  const allChecked = mod.actions.every((a) => selectedPermissions.has(a.code));
                  return (
                    <div key={appKey} style={{ marginBottom: "16px", background: "#fff", border: "1px solid #e5eaf3", borderRadius: "12px", padding: "14px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
                        {logo && (
                          <img src={logo} alt="" style={{ width: "26px", height: "26px", objectFit: "contain", borderRadius: "6px" }} />
                        )}
                        <p style={{ margin: 0, fontSize: "14px", fontWeight: 800, color: "#172033" }}>{appName}</p>
                      </div>

                      <div style={{ overflowX: "auto" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "420px" }}>
                          <thead>
                            <tr>
                              <th style={{ textAlign: "left", padding: "6px 10px", fontSize: "12px", color: "#64748b" }}>Access</th>
                              {ACTION_ORDER.map((action) => (
                                <th key={action} style={{ textAlign: "center", padding: "6px 10px", fontSize: "12px", color: "#64748b", textTransform: "capitalize" }}>
                                  {action}
                                </th>
                              ))}
                              <th style={{ textAlign: "center", padding: "6px 10px", fontSize: "12px", color: "#64748b" }}>All</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr style={{ borderTop: "1px solid #e5eaf3" }}>
                              <td style={{ padding: "8px 10px", fontSize: "13px", fontWeight: 700, color: "#172033" }}>All pages in this app</td>
                              {ACTION_ORDER.map((action) => {
                                const entry = mod.actions.find((a) => a.action === action);
                                return (
                                  <td key={action} style={{ textAlign: "center", padding: "8px 10px" }}>
                                    {entry && (
                                      <input
                                        type="checkbox"
                                        checked={selectedPermissions.has(entry.code)}
                                        onChange={() => togglePermission(entry.code)}
                                      />
                                    )}
                                  </td>
                                );
                              })}
                              <td style={{ textAlign: "center", padding: "8px 10px" }}>
                                <input
                                  type="checkbox"
                                  checked={allChecked}
                                  onChange={(e) => toggleAllActionsForModule(mod.actions, e.target.checked)}
                                />
                              </td>
                            </tr>

                            {/* Individual pages: leave these unchecked to keep members on the
                                "All pages" grant above; check any of them and this group will
                                restrict members of the app to only the pages checked here. */}
                            {pageItems.map((page) => {
                              const pageActions = ACTION_ORDER.map((action) => ({
                                action,
                                code: pagePermissionCode(mod.service_name, page.path, action),
                              }));
                              const pageAllChecked = pageActions.every((a) => selectedPermissions.has(a.code));
                              return (
                                <tr key={page.path} style={{ borderTop: "1px solid #f1f5f9" }}>
                                  <td style={{ padding: "6px 10px 6px 22px", fontSize: "13px", color: "#64748b" }}>{page.name}</td>
                                  {pageActions.map(({ action, code }) => (
                                    <td key={action} style={{ textAlign: "center", padding: "6px 10px" }}>
                                      <input
                                        type="checkbox"
                                        checked={selectedPermissions.has(code)}
                                        onChange={() => togglePermission(code)}
                                      />
                                    </td>
                                  ))}
                                  <td style={{ textAlign: "center", padding: "6px 10px" }}>
                                    <input
                                      type="checkbox"
                                      checked={pageAllChecked}
                                      onChange={(e) => toggleAllActionsForModule(pageActions, e.target.checked)}
                                    />
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      {mod.endpoints && mod.endpoints.length > 0 && (
                        <div style={{ marginTop: "10px" }}>
                          <button
                            type="button"
                            onClick={() => setExpandedApiOps((prev) => ({ ...prev, [appKey]: !prev[appKey] }))}
                            style={{
                              background: "none",
                              border: "none",
                              padding: "4px 0",
                              cursor: "pointer",
                              fontSize: "12px",
                              fontWeight: 700,
                              color: "#2e7d32",
                            }}
                          >
                            {expandedApiOps[appKey] ? "▾" : "▸"} API operations behind this app ({mod.endpoints.length})
                          </button>

                          {expandedApiOps[appKey] && (
                            <>
                              <p style={{ margin: "4px 0 8px", fontSize: "12px", color: "#7c8aa1" }}>
                                What this app's pages actually call behind the scenes, broken out by purpose (e.g.
                                "Purchase Order" covers the create/edit/delete calls for purchase orders
                                specifically). Leave these unchecked to stay on the "All pages"/page-level access
                                above; check any of them and only the operations you've checked here will be
                                allowed for this module, regardless of what's granted above.
                              </p>
                              <div style={{ overflowX: "auto" }}>
                                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "420px" }}>
                                  <thead>
                                    <tr>
                                      <th style={{ textAlign: "left", padding: "6px 10px", fontSize: "12px", color: "#64748b" }}>Operation</th>
                                      {ACTION_ORDER.map((action) => (
                                        <th key={action} style={{ textAlign: "center", padding: "6px 10px", fontSize: "12px", color: "#64748b", textTransform: "capitalize" }}>
                                          {action}
                                        </th>
                                      ))}
                                      <th style={{ textAlign: "center", padding: "6px 10px", fontSize: "12px", color: "#64748b" }}>All</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {mod.endpoints.map((ep) => {
                                      const epAllChecked = ep.actions.every((a) => selectedPermissions.has(a.code));
                                      return (
                                        <tr key={ep.endpoint} style={{ borderTop: "1px solid #f1f5f9" }}>
                                          <td style={{ padding: "6px 10px", fontSize: "13px", color: "#64748b" }}>{ep.label}</td>
                                          {ep.actions.map((a) => (
                                            <td key={a.action} style={{ textAlign: "center", padding: "6px 10px" }}>
                                              <input
                                                type="checkbox"
                                                checked={selectedPermissions.has(a.code)}
                                                onChange={() => togglePermission(a.code)}
                                              />
                                            </td>
                                          ))}
                                          <td style={{ textAlign: "center", padding: "6px 10px" }}>
                                            <input
                                              type="checkbox"
                                              checked={epAllChecked}
                                              onChange={(e) => toggleAllActionsForModule(ep.actions, e.target.checked)}
                                            />
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}

                {otherModules.length > 0 && (
                  <div style={{ marginTop: "10px" }}>
                    <p style={{ margin: "0 0 8px", fontSize: "12px", fontWeight: 800, letterSpacing: "0.04em", textTransform: "uppercase", color: "#7c8aa1" }}>
                      Other (not tied to a specific app page)
                    </p>
                    <div style={{ overflowX: "auto" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "420px" }}>
                        <thead>
                          <tr>
                            <th style={{ textAlign: "left", padding: "6px 10px", fontSize: "12px", color: "#64748b" }}>Module</th>
                            {ACTION_ORDER.map((action) => (
                              <th key={action} style={{ textAlign: "center", padding: "6px 10px", fontSize: "12px", color: "#64748b", textTransform: "capitalize" }}>
                                {action}
                              </th>
                            ))}
                            <th style={{ textAlign: "center", padding: "6px 10px", fontSize: "12px", color: "#64748b" }}>All</th>
                          </tr>
                        </thead>
                        <tbody>
                          {otherModules.map((mod) => {
                            const allChecked = mod.actions.every((a) => selectedPermissions.has(a.code));
                            return (
                              <tr key={mod.service_name} style={{ borderTop: "1px solid #e5eaf3" }}>
                                <td style={{ padding: "8px 10px", fontSize: "14px", fontWeight: 700, color: "#172033" }}>{mod.label}</td>
                                {ACTION_ORDER.map((action) => {
                                  const entry = mod.actions.find((a) => a.action === action);
                                  return (
                                    <td key={action} style={{ textAlign: "center", padding: "8px 10px" }}>
                                      {entry && (
                                        <input
                                          type="checkbox"
                                          checked={selectedPermissions.has(entry.code)}
                                          onChange={() => togglePermission(entry.code)}
                                        />
                                      )}
                                    </td>
                                  );
                                })}
                                <td style={{ textAlign: "center", padding: "8px 10px" }}>
                                  <input
                                    type="checkbox"
                                    checked={allChecked}
                                    onChange={(e) => toggleAllActionsForModule(mod.actions, e.target.checked)}
                                  />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>

              <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
                <button type="button" className="store-config-reset" onClick={resetGroupForm}>
                  Cancel
                </button>
                <button type="submit" className="store-config-save" disabled={saving}>
                  {saving ? "Saving..." : editingGroupId ? "Save Changes" : "Create Group"}
                </button>
              </div>
            </form>
          )}

          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "480px" }}>
              <thead>
                <tr style={{ background: "#f8fafc" }}>
                  <th style={{ textAlign: "left", padding: "10px 14px", fontSize: "12px", color: "#64748b" }}>Group</th>
                  <th style={{ textAlign: "left", padding: "10px 14px", fontSize: "12px", color: "#64748b" }}>Permissions granted</th>
                  <th style={{ textAlign: "right", padding: "10px 14px", fontSize: "12px", color: "#64748b" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {groups.length === 0 && (
                  <tr>
                    <td colSpan={3} style={{ padding: "18px 14px", color: "#94a3b8", fontSize: "14px" }}>
                      No permission groups yet. Create one above to start restricting what team members can do.
                    </td>
                  </tr>
                )}
                {groups.map((group) => (
                  <tr key={group.id} style={{ borderTop: "1px solid #e5eaf3" }}>
                    <td style={{ padding: "12px 14px", fontWeight: 800, color: "#172033" }}>{group.name}</td>
                    <td style={{ padding: "12px 14px", color: "#64748b", fontSize: "13px" }}>
                      {(group.permissions || []).length} permission{(group.permissions || []).length === 1 ? "" : "s"} granted
                    </td>
                    <td style={{ padding: "12px 14px", textAlign: "right", whiteSpace: "nowrap" }}>
                      <button type="button" className="store-config-reset" style={{ minWidth: "auto", padding: "8px 14px", marginRight: "8px" }} onClick={() => startEditGroup(group)}>
                        Edit
                      </button>
                      <button type="button" className="store-config-reset" style={{ minWidth: "auto", padding: "8px 14px", background: "#fef2f2", color: "#b91c1c", boxShadow: "inset 0 0 0 1px #fecaca" }} onClick={() => handleDeleteGroup(group)}>
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {!loading && activeTab === "members" && (
        <div style={{ overflowX: "auto" }}>
          {groups.length === 0 && (
            <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: "12px", padding: "14px 16px", marginBottom: "18px", color: "#78350f", fontSize: "14px" }}>
              You haven't created any Permission Groups yet — create one in the "Permission Groups" tab first, then come back here to add team members to it.
            </div>
          )}
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "600px" }}>
            <thead>
              <tr style={{ background: "#f8fafc" }}>
                <th style={{ textAlign: "left", padding: "10px 14px", fontSize: "12px", color: "#64748b" }}>Team member</th>
                <th style={{ textAlign: "left", padding: "10px 14px", fontSize: "12px", color: "#64748b" }}>Role</th>
                <th style={{ textAlign: "left", padding: "10px 14px", fontSize: "12px", color: "#64748b" }}>Permission groups</th>
                <th style={{ textAlign: "right", padding: "10px 14px", fontSize: "12px", color: "#64748b" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {members.length === 0 && (
                <tr>
                  <td colSpan={4} style={{ padding: "18px 14px", color: "#94a3b8", fontSize: "14px" }}>
                    No team members found for this store.
                  </td>
                </tr>
              )}
              {members.map((member) => {
                const isOwner = member.role === "owner";
                const draft = memberDraftGroups[member.id] || new Set();
                return (
                  <tr key={member.id} style={{ borderTop: "1px solid #e5eaf3", verticalAlign: "top" }}>
                    <td style={{ padding: "12px 14px" }}>
                      <div style={{ fontWeight: 800, color: "#172033" }}>{member.user_name || member.user_email}</div>
                      <div style={{ fontSize: "12px", color: "#94a3b8" }}>{member.user_email}</div>
                    </td>
                    <td style={{ padding: "12px 14px", textTransform: "capitalize", color: "#374151" }}>{member.role}</td>
                    <td style={{ padding: "12px 14px" }}>
                      {isOwner ? (
                        <span style={{ fontSize: "13px", color: "#94a3b8" }}>Owners always have full access</span>
                      ) : groups.length === 0 ? (
                        <span style={{ fontSize: "13px", color: "#94a3b8" }}>—</span>
                      ) : (
                        <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                          {groups.map((group) => (
                            <label key={group.id} style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", color: "#374151" }}>
                              <input
                                type="checkbox"
                                checked={draft.has(String(group.id))}
                                onChange={() => toggleMemberGroup(member.id, group.id)}
                              />
                              {group.name}
                            </label>
                          ))}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: "12px 14px", textAlign: "right" }}>
                      {!isOwner && (
                        <button
                          type="button"
                          className="store-config-save"
                          style={{ minWidth: "auto", padding: "8px 14px" }}
                          disabled={savingMemberId === member.id}
                          onClick={() => handleSaveMemberGroups(member)}
                        >
                          {savingMemberId === member.id ? "Saving..." : "Save"}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PermissionGroupsSettings;
